import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import withRouter from '../../components/withRouter';

// Import all the new components
import CampaignInfoCard, { CampaignInfoCardRef } from './components/CampaignInfoCard';
import GeneralSummaryCard, { GeneralSummaryCardRef } from './components/GeneralSummaryCard';
import DeliveryStatusCard, { DeliveryStatusCardRef } from './components/DeliveryStatusCard';
import PricingModelSummaryCard from './components/PricingModelSummaryCard';
import DeliveryReportTabs, { DeliveryReportTabsRef } from './components/DeliveryReportTabs';
import DeviceReportCard, { DeviceReportCardRef } from './components/DeviceReportCard';
import PageAccessCard, { PageAccessCardRef } from './components/PageAccessCard';
import PageBlockedCard, { PageBlockedCardRef } from './components/PageBlockedCard';
import CategoryAccessCard, { CategoryAccessCardRef } from './components/CategoryAccessCard';
import StateMapCard, { StateMapCardRef } from './components/StateMapCard';
import CountryMapCard, { CountryMapCardRef } from './components/CountryMapCard';
import DeliveryByIpCard, { DeliveryByIpCardRef } from './components/DeliveryByIpCard';
import DeliveryByUserCard, { DeliveryByUserCardRef } from './components/DeliveryByUserCard';
import HeatmapCard, { HeatmapCardRef } from './components/HeatmapCard';
import ReferrerCard, { ReferrerCardRef } from './components/ReferrerCard';
import DownloadReportModal from './DownloadReportModal';
import { getRelatorioPorTipoDeCompra } from '../../api/reportApi';
import { getCampaign } from '../../api/campaignApi';
import { Campaign } from '../../types/campaign';

import '../../assets/scss/pages/relatorio.scss';
import './components/DropdownOverrides.css';

const Index = (props: any) => {
    const { campaignId } = useParams();
    const [isDownloadReportModalOpen, setIsDownloadReportModalOpen] = useState<boolean>(false);
    const [pricingModelData, setPricingModelData] = useState<any>(null);
    const [campaign, setCampaign] = useState<Campaign | null>(null);

    // Refs for all components that can be refreshed
    const campaignRef = useRef<CampaignInfoCardRef>(null);
    const generalRef = useRef<GeneralSummaryCardRef>(null);
    const deliveryStatusRef = useRef<DeliveryStatusCardRef>(null);
    const deliveryRef = useRef<DeliveryReportTabsRef>(null);
    const deviceRef = useRef<DeviceReportCardRef>(null);
    const pageAccessRef = useRef<PageAccessCardRef>(null);
    const pageBlockedRef = useRef<PageBlockedCardRef>(null);
    const categoryRef = useRef<CategoryAccessCardRef>(null);
    const stateRef = useRef<StateMapCardRef>(null);
    const countryRef = useRef<CountryMapCardRef>(null);
    const ipRef = useRef<DeliveryByIpCardRef>(null);
    const userRef = useRef<DeliveryByUserCardRef>(null);
    const heatmapRef = useRef<HeatmapCardRef>(null);
    const referrerRef = useRef<ReferrerCardRef>(null);

    // Fetch campaign data
    const fetchCampaign = useCallback(async () => {
        try {
            const campaignData = await getCampaign(parseInt(campaignId || '0'));
            setCampaign(campaignData);
        } catch (error) {
            console.error('Error fetching campaign:', error);
        }
    }, [campaignId]);

    // Fetch pricing model data for CPM/CPV/CPC cards
    const fetchPricingModelData = useCallback(async () => {
        try {
            const data = await getRelatorioPorTipoDeCompra(parseInt(campaignId || '0'));
            setPricingModelData(data);
        } catch (error) {
            console.error('Error fetching pricing model data:', error);
        }
    }, [campaignId]);


    const toggleDownloadReportModal = () => {
        setIsDownloadReportModalOpen(!isDownloadReportModalOpen);
    };

    // Function to refresh all reports
    const refreshAllReports = () => {
        // Refresh all components that have refresh methods
        campaignRef.current?.refresh();
        generalRef.current?.refresh();
        deliveryStatusRef.current?.refresh();
        deliveryRef.current?.refresh();
        deviceRef.current?.refresh();
        pageAccessRef.current?.refresh();
        pageBlockedRef.current?.refresh();
        categoryRef.current?.refresh();
        stateRef.current?.refresh();
        countryRef.current?.refresh();
        ipRef.current?.refresh();
        userRef.current?.refresh();
        heatmapRef.current?.refresh();
        referrerRef.current?.refresh();

        // Also refresh pricing model data
        fetchPricingModelData();
    };

    // Fetch initial data
    useEffect(() => {
        if (campaignId) {
            fetchCampaign();
            fetchPricingModelData();
        }
    }, [campaignId, fetchCampaign, fetchPricingModelData]);

    if (!campaignId) {
        return (
            <div className="page-content">
                <div className="container-fluid">
                    <div className="text-center">
                        <h4>ID da campanha não encontrado</h4>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <React.Fragment>
            <div className="page-content">
                <div className="container-fluid">
                    {/* Row 1: Campaign Info */}
                    <div className="row mb-4">
                        <div className="col">
                            <CampaignInfoCard
                                campaignId={campaignId}
                                ref={campaignRef}
                                onDownloadReport={toggleDownloadReportModal}
                                onRefreshAll={refreshAllReports}
                            />
                        </div>
                    </div>

                    {/* Row 3: Delivery Status */}
                    {/* <div className="row mb-4">
                        <div className="col">
                            <DeliveryStatusCard
                                campaignId={campaignId}
                                ref={deliveryStatusRef}
                            />
                        </div>
                    </div> */}

                    {/* Row 2: General Summary */}
                    <div className="row mb-4">
                        <div className="col">
                            <GeneralSummaryCard
                                data={pricingModelData}
                                loading={!pricingModelData}
                                onRefresh={fetchPricingModelData}
                                ref={generalRef}
                            />
                        </div>
                    </div>

                    {/* Rows 4-6: Pricing Model Summaries (CPM, CPV, CPC) */}
                    {pricingModelData?.cpm?.purchaseImpressions > 0 && (
                        <div className="row mb-4">
                            <div className="col">
                                <PricingModelSummaryCard
                                    pricingModel="CPM"
                                    data={pricingModelData.cpm}
                                    onRefresh={fetchPricingModelData}
                                    loading={false}
                                />
                            </div>
                        </div>
                    )}

                    {pricingModelData?.cpv?.purchaseImpressions > 0 && (
                        <div className="row mb-4">
                            <div className="col">
                                <PricingModelSummaryCard
                                    pricingModel="CPV"
                                    data={pricingModelData.cpv}
                                    onRefresh={fetchPricingModelData}
                                    loading={false}
                                />
                            </div>
                        </div>
                    )}

                    {pricingModelData?.cpc?.purchaseImpressions > 0 && (
                        <div className="row mb-4">
                            <div className="col">
                                <PricingModelSummaryCard
                                    pricingModel="CPC"
                                    data={pricingModelData.cpc}
                                    onRefresh={fetchPricingModelData}
                                    loading={false}
                                />
                            </div>
                        </div>
                    )}

                    {/* Row 6: Delivery Reports */}
                    <div className="row mb-4">
                        <div className="col">
                            <DeliveryReportTabs
                                campaignId={campaignId}
                                ref={deliveryRef}
                            />
                        </div>
                    </div>

                    {/* Row 7: Pages Reports */}
                    <div className="row mb-4">
                        <div className="col-lg-4">
                            <DeviceReportCard
                                campaignId={campaignId}
                                ref={deviceRef}
                            />
                        </div>
                        <div className="col-lg-4">
                            <PageAccessCard
                                campaignId={campaignId}
                                ref={pageAccessRef}
                            />
                        </div>
                        <div className="col-lg-4">
                            <PageBlockedCard
                                campaignId={campaignId}
                                ref={pageBlockedRef}
                            />
                        </div>
                    </div>

                    {/* Row 8: Heatmap and Referrer */}
                    <div className="row mb-4">
                        <div className="col-lg-8">
                            <HeatmapCard
                                campaignId={campaignId}
                                ref={heatmapRef}
                            />
                        </div>
                        <div className="col-lg-4">
                            <ReferrerCard
                                campaignId={campaignId}
                                ref={referrerRef}
                            />
                        </div>
                    </div>

                    {/* Row 9: Categories and State Map */}
                    <div className="row mb-4">
                        <div className="col-lg-4">
                            <CategoryAccessCard
                                campaignId={campaignId}
                                ref={categoryRef}
                            />
                        </div>
                        <div className="col-lg-8">
                            <StateMapCard
                                campaignId={campaignId}
                                ref={stateRef}
                            />
                        </div>
                    </div>

                    {/* Row 10: Country Map */}
                    <div className="row mb-4">
                        <div className="col">
                            <CountryMapCard
                                campaignId={campaignId}
                                ref={countryRef}
                            />
                        </div>
                    </div>

                    {/* Row 11: IP and User Delivery */}
                    <div className="row mb-4">
                        <div className="col-lg-6">
                            <DeliveryByIpCard
                                campaignId={campaignId}
                                ref={ipRef}
                            />
                        </div>
                        <div className="col-lg-6">
                            <DeliveryByUserCard
                                campaignId={campaignId}
                                ref={userRef}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Download Report Modal */}
            {campaign && (
                <DownloadReportModal
                    campaign={campaign}
                    isOpen={isDownloadReportModalOpen}
                    toggle={toggleDownloadReportModal}
                />
            )}
        </React.Fragment>
    );
};

export default withRouter(Index);
